/**
 * EventContent.js
 *
 * Content section for generic event cards and artist event cards.
 * Handles both ARTIST_EVENT and EVENT content types.
 */

import React, { memo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import EntityService from '../../../services/entityService';
import VenueFollowButton from '../../shared/VenueFollowButton';
import UitInVlaanderenCredit from '../../shared/UitInVlaanderenCredit';
import { formatCurrencyRange } from '../../../utils/currencyFormat';

// Generic type/taxonomy words that don't add information beyond the broad
// category (some sources store a type word like "concert" in `genre`).
const GENERIC_GENRE_WORDS = new Set([
  'concert', 'concerts', 'sport', 'sports', 'theater', 'theatre',
  'event', 'events', 'misc', 'miscellaneous', 'other', 'general', 'music'
]);

const EventContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const { title, location, typeData } = data;
  const {
    venue,
    venueId,
    venueIsFollowing,
    hideVenueFollow,
    city,
    state,
    countryCode,
    category,
    genre,
    description,
    price,
    priceMin,
    priceMax,
    priceCurrency,
    source,
    matchType,
    matchReason,
    matchedArtist,
    matchedEntityId,
    artists
  } = typeData || {};

  // Prefer formatting from structured price fields (proper currency symbols via
  // Intl); fall back to the server's price_display string only when we have no
  // numeric min/max. The "See tickets" placeholder never reaches here — it is
  // dropped at normalization (realPriceDisplay in normalizeCardData).
  const priceLabel = formatCurrencyRange(priceMin, priceMax, priceCurrency)
    || (typeof price === 'string' ? price : (price != null ? t('eventContent.priceFrom', { price }) : ''));

  // Enriched category line: append the genre only when it adds information —
  // present, different from the broad category, and not a generic type word
  // (a type word like "concert" in `genre` is noise here).
  const rawGenre = (genre || '').trim();
  const genreLabel = (
    rawGenre &&
    rawGenre.toLowerCase() !== (category || '').toLowerCase() &&
    !GENERIC_GENRE_WORDS.has(rawGenre.toLowerCase())
  ) ? rawGenre : null;
  const categoryDisplay = category
    ? (genreLabel ? `${category} · ${genreLabel}` : category)
    : null;

  // Body summary: the real description when we have one (Ticketmaster events
  // often do), otherwise a short synthesized line from genre + venue so
  // description-less sources still get some body text.
  const trimmedDescription = (description || '').trim();
  const summary = trimmedDescription
    || (venue
      ? t('eventContent.summaryFallback', {
          category: (genreLabel || category || t('eventContent.genericEvent')).toLowerCase(),
          venue
        })
      : '');

  const [addedToFavorites, setAddedToFavorites] = useState(false);
  const [adding, setAdding] = useState(false);

  // Show the button for similar/tribute matches where we have an entity ID
  // Don't show for "direct" matches — user already has that artist as a favorite
  const showAddFavorite = matchedEntityId && matchType && matchType !== 'direct' && !addedToFavorites;

  const handleAddFavorite = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (adding || addedToFavorites) return;

    setAdding(true);
    const result = await EntityService.saveUserPreference(matchedEntityId, 2);
    setAdding(false);

    if (result.success) {
      setAddedToFavorites(true);
      // Notify the feed to refresh so new favorites are reflected in rankings
      window.dispatchEvent(new CustomEvent('favorite-artist-added', { detail: { entityId: matchedEntityId, artistName: matchedArtist } }));
    }
  }, [matchedEntityId, matchedArtist, adding, addedToFavorites]);

  // Build the city/state suffix appended after the venue name.
  // US events: "City, ST" — others: "City". Returns '' when no city.
  const buildCitySuffix = () => {
    if (!city) return '';
    const isUS = countryCode === 'US';
    return isUS && state ? `${city}, ${state}` : city;
  };
  const citySuffix = buildCitySuffix();
  // Fallback display when we have neither structured venue+city nor anything to link.
  const fallbackLocation = venue || city || location || '';

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-2">{title}</h3>

      {/* Venue / Location. When we have a canonical venueId, the venue name
          is a link to its page and a small follow-venue chip sits inline. */}
      {(venue || fallbackLocation) && (
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-sm text-content-secondary line-clamp-1 flex-1 min-w-0">
            <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />
            {venue ? (
              <>
                {venueId ? (
                  <Link
                    to={`/venues/${encodeURIComponent(venueId)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-primary transition-colors"
                  >
                    {venue}
                  </Link>
                ) : (
                  venue
                )}
                {citySuffix ? `, ${citySuffix}` : ''}
              </>
            ) : (
              fallbackLocation
            )}
          </p>
          {/* Hidden on the venue's own page (the hero owns the follow there);
              initial state comes from the payload because this button never
              fetches — a false default reads "+ Follow" on followed venues
              and a second tap would then silently unfollow. */}
          {venueId && !hideVenueFollow && (
            <VenueFollowButton venueId={venueId} size="sm" initialIsFollowing={!!venueIsFollowing} />
          )}
        </div>
      )}

      {/* Artist match indicator with optional quick-add favorite button */}
      {matchReason && (
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-xs text-primary font-medium line-clamp-1 flex-1 min-w-0">
            {matchType === 'direct' ? <Icon name="microphone" className="w-3 h-3 inline" /> : <Icon name="link" className="w-3 h-3 inline" />}{' '}{matchReason}
          </p>
          {showAddFavorite && (
            <button
              onClick={handleAddFavorite}
              disabled={adding}
              className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-lighter text-primary hover:bg-primary hover:text-content-inverse transition-colors"
              title={t('eventContent.favorite.addButtonTitle', { matchedArtist })}
              aria-label={t('eventContent.favorite.addButtonTitle', { matchedArtist })}
            >
              <Icon name="plus" className="w-3 h-3" />
              <span className="hidden sm:inline">{t('eventContent.favorite.addButtonLabel')}</span>
            </button>
          )}
          {addedToFavorites && (
            <span className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
              <Icon name="check" className="w-3 h-3" />
              <span className="hidden sm:inline">{t('eventContent.favorite.addedLabel')}</span>
            </span>
          )}
        </div>
      )}

      {/* Artist lineup (for artist events with multiple artists) */}
      {artists && artists.length > 1 && (
        <p className="text-xs text-content-muted mb-2 line-clamp-1">
          <Icon name="musicalNote" className="w-3 h-3 inline mr-1" />{artists.map(a => a.name || a).join(', ')}
        </p>
      )}

      {/* Category / Genre */}
      {categoryDisplay && !matchReason && (
        <p className="text-xs text-primary font-medium mb-2 line-clamp-1">
          <Icon name="ticket" className="w-3 h-3 inline mr-1" />{categoryDisplay}
        </p>
      )}

      {/* Summary — real description, or a synthesized genre/venue line */}
      {summary && (
        <p className="text-xs text-content-muted mb-2 line-clamp-2">
          {summary}
        </p>
      )}

      {/* Price */}
      {priceLabel && (
        <p className="text-xs text-content-muted mb-2">
          {priceLabel}
        </p>
      )}

      {/* Source credit required by publiq's terms — per card, because the
          mixed infinite feed has no footer these cards are reliably near. */}
      {source === 'uitdatabank' && <UitInVlaanderenCredit variant="inline" />}
    </div>
  );
};

export default memo(EventContent);
